import { SectionCard } from '@/components/SectionCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useConfirm } from '@/hooks/useConfirm'
import { useDebounce } from '@/hooks/useDebounce'
import { isApiError } from '@/utils/error'
import { showError, showSuccess } from '@/utils/toast'
import { Edit2, Eye, Plus, Search, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDeleteVendor, useGetVendors } from '../hooks/useVendors'
import { Vendor } from '../types/vendor'
import { VendorDetailDialog } from './VendorDetailDialog'
import { VendorDialog } from './VendorDialog'

export function VendorsView() {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)
  const { data: vendors = [], isLoading } = useGetVendors(debouncedSearch)
  const deleteVendor = useDeleteVendor()
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const { isAdmin } = useAuth()

  const canManage = isAdmin

  const handleDeleteVendor = async (vendorId: string) => {
    const confirmed = await confirm({
      variant: 'destructive',
      title: t('common.deleteConfirmTitle'),
      description: t('vendors.deleteConfirmDescription'),
    })

    if (confirmed) {
      try {
        await deleteVendor.mutateAsync(vendorId)
        showSuccess(t('vendors.deleteSuccess'))
      } catch (error) {
        if (isApiError(error)) showError(error.message)
      }
    }
  }

  return (
    <>
      <SectionCard
        title={`${t('vendors.title')} (${vendors.length})`}
        right={
          <>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="vendors-search-input"
                placeholder={t('vendors.searchPlaceholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 w-[200px] bg-secondary/50 pl-8 text-xs"
              />
            </div>
            {canManage && (
              <Button
                data-testid="vendors-create-button"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  setSelectedVendor(null)
                  setDialogOpen(true)
                }}
              >
                <Plus className="mr-1 size-3" />
                {t('vendors.addVendor')}
              </Button>
            )}
          </>
        }
      >
        <div data-testid="vendors-table" className="rounded-lg border border-border/30">
          <Table className="table-fixed w-full">
            <TableHeader>
              <TableRow className="border-border/30 hover:bg-transparent">
                <TableHead className="text-xs text-muted-foreground">{t('vendors.companyName')}</TableHead>
                <TableHead className="text-xs text-muted-foreground">{t('vendors.contact')}</TableHead>
                <TableHead className="text-xs text-muted-foreground">{t('vendors.phone')}</TableHead>
                <TableHead className="text-xs text-muted-foreground">{t('vendors.email')}</TableHead>
                <TableHead className="text-xs text-muted-foreground">{t('vendors.products')}</TableHead>
                <TableHead className="text-xs text-muted-foreground">{t('common.equipment')}</TableHead>
                {vendors.length !== 0 && <TableHead className="sr-only text-xs">{t('common.actions')}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </TableCell>
                </TableRow>
              ) : vendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                    {t('vendors.noVendors')}
                  </TableCell>
                </TableRow>
              ) : (
                vendors.map(vendor => {
                  return (
                    <TableRow key={vendor.id} data-vendor-id={vendor.id} className="border-border/20 hover:bg-secondary/40">
                      <TableCell className="wrap-break-word text-xs font-medium text-foreground">
                        {vendor.name}
                      </TableCell>
                      <TableCell className="wrap-break-word text-xs text-muted-foreground">
                        {vendor.contactName || '-'}
                      </TableCell>
                      <TableCell className="wrap-break-word text-xs text-muted-foreground">
                        {vendor.phone || '-'}
                      </TableCell>
                      <TableCell className="wrap-break-word text-xs text-muted-foreground">
                        {vendor.email || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">
                          {vendor.productCount} {t('vendors.itemUnit')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">
                          {vendor.itemCount} {t('common.equipmentUnit')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            data-testid="vendor-view-button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[10px] text-primary"
                            onClick={() => {
                              setSelectedVendor(vendor)
                              setDetailOpen(true)
                            }}
                          >
                            <Eye className="mr-1 size-3" />
                            {t('common.view')}
                          </Button>
                          {canManage && (
                            <>
                              <Button
                                data-testid="vendor-edit-button"
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-[10px] text-muted-foreground"
                                onClick={() => {
                                  setSelectedVendor(vendor)
                                  setDialogOpen(true)
                                }}
                              >
                                <Edit2 className="mr-1 size-3" />
                                {t('common.edit')}
                              </Button>
                              <Button
                                data-testid="vendor-delete-button"
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-[10px] text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleDeleteVendor(vendor.id)}
                                disabled={deleteVendor.isPending}
                              >
                                <Trash2 className="mr-1 size-3" />
                                {t('common.delete')}
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      <VendorDialog vendor={selectedVendor} open={dialogOpen} onOpenChange={setDialogOpen} />
      <VendorDetailDialog vendor={selectedVendor} open={detailOpen} onOpenChange={setDetailOpen} />
    </>
  )
}
